import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {NavigationLine} from '../../../../../../../src/components/common/navigation/navigationLine';
import {NavigationLineIcon} from '../../../../../../../src/components/common/navigation/navigationLineIcon';
import {NavigationLineLabel} from '../../../../../../../src/components/common/navigation/navigationLineLabel';
import {ContactsManagerParametersTypesEnum} from '../../../../../../../src/store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {HandlerOf} from '../../../../../../shared/helpers/src/react/reactHelpers';
import {Icon, IconName} from '../../../../../../shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../shared/ui/src/styles/fontStyles';
import {UserTheme, useUserTheme} from '../../../../../../shared/ui/src/theme/userThemeProvider';

/*
 * Constants.
 */

const contactsNavigationSection: NavigationSection = {
  title: <Trans id="contacts-manager-navigation-contacts">Contacts</Trans>,
  navigationItems: [
    {
      type: ContactsManagerParametersTypesEnum.COMPANY_CONTACTS,
      iconName: 'meetingBuildingSmall',
      selectedIconName: 'buildingFilledSmall',
      name: <Trans id="contacts-manager-navigation-company-contacts">Company contacts</Trans>,
    },
    {
      type: ContactsManagerParametersTypesEnum.PRIVATE_CONTACTS,
      iconName: 'lockOutlineSmall',
      selectedIconName: 'lockSmall',
      name: <Trans id="contacts-manager-navigation-private-contacts">Private contacts</Trans>,
    },
    {
      type: ContactsManagerParametersTypesEnum.OTHER_CONTACTS,
      iconName: 'sparklesOutlineSmall',
      selectedIconName: 'sparklesSmall',
      name: <Trans id="contacts-manager-navigation-other-contacts">Other contacts</Trans>,
    },
    {
      type: ContactsManagerParametersTypesEnum.LISTS,
      subPageTypes: [ContactsManagerParametersTypesEnum.LIST_CONTACTS],
      iconName: 'listSmall',
      selectedIconName: 'listFilledSmall',
      name: <Trans id="contacts-manager-navigation-lists">Lists</Trans>,
    },
  ],
};

const accountsNavigationSection: NavigationSection = {
  title: <Trans id="contacts-manager-navigation-accounts">Accounts</Trans>,
  navigationItems: [
    {
      type: ContactsManagerParametersTypesEnum.ACCOUNTS,
      subPageTypes: [
        ContactsManagerParametersTypesEnum.ACCOUNT_CONTACTS,
        ContactsManagerParametersTypesEnum.ACCOUNTS_CSV_IMPORT,
      ],
      iconName: 'briefcaseOutlineSmall',
      selectedIconName: 'briefcaseFilledSmall',
      name: <Trans id="contacts-manager-navigation-accounts">Accounts</Trans>,
    },
  ],
};

/*
 * Types.
 */

interface NavigationSection {
  title: ReactNode;
  navigationItems: ReadonlyArray<NavigationItem>;
}

interface NavigationItem {
  type: ContactsManagerParametersTypesEnum;
  subPageTypes?: ReadonlyArray<ContactsManagerParametersTypesEnum>;
  iconName: IconName;
  selectedIconName: IconName;
  name: ReactNode;
}

interface ContactsManagerNavigationProps {
  currentRouteType: ContactsManagerParametersTypesEnum | undefined;
  hasNewContactsManagerAccounts: boolean;
  onNavigate: HandlerOf<ContactsManagerParametersTypesEnum>;
}

/*
 * Style.
 */

interface StyledNavigationHeaderDivProps {
  $color: string;
}

const StyledNavigationHeaderDiv = styled.div<StyledNavigationHeaderDivProps>`
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.$color};
  padding: 6px 8px 6px 14px;
`;

const StyledNavigationSectionDiv = styled.div`
  &:not(:last-child) {
    margin-bottom: 20px;
  }
`;

/*
 * Component.
 */

export const ContactsManagerNavigationItems: FC<ContactsManagerNavigationProps> = (props) => {
  const {hasNewContactsManagerAccounts} = props;
  const theme = useUserTheme();

  return (
    <>
      {renderNavigationSection(contactsNavigationSection, props, theme)}
      {hasNewContactsManagerAccounts && renderNavigationSection(accountsNavigationSection, props, theme)}
    </>
  );
};

function renderNavigationSection(
  section: NavigationSection,
  props: ContactsManagerNavigationProps,
  theme: UserTheme,
): ReactNode {
  return (
    <StyledNavigationSectionDiv>
      <StyledNavigationHeaderDiv $color={theme.header}>{section.title}</StyledNavigationHeaderDiv>
      {section.navigationItems.map((item) => renderNavigationItem(item, props, theme))}
    </StyledNavigationSectionDiv>
  );
}

function renderNavigationItem(
  navigationItem: NavigationItem,
  props: ContactsManagerNavigationProps,
  theme: UserTheme,
): ReactNode {
  const {type, subPageTypes, iconName, selectedIconName, name} = navigationItem;
  const {currentRouteType, onNavigate} = props;
  const isBasePageSelected = type === currentRouteType;
  const isSubPageSelected = Boolean(
    subPageTypes && currentRouteType && subPageTypes.includes(currentRouteType),
  );
  const isSelected = isBasePageSelected || isSubPageSelected;
  return (
    <NavigationLine isSelected={isSelected} key={type} onClick={() => onNavigate(type)}>
      <NavigationLineIcon>
        <Icon name={isSelected ? selectedIconName : iconName} color={theme.icon} />
      </NavigationLineIcon>
      <NavigationLineLabel>{name}</NavigationLineLabel>
    </NavigationLine>
  );
}
